/* vm-sourcing-form.js — submit handler for the empty-collection sourcing form
   (snippets/vm-empty-sourcing.liquid).

   BACKEND CONTRACT (valveman-storefront-api, lib/handleQuote.js): POST JSON to the
   form's data-endpoint. Field names must match EXACTLY: name/email/message (required,
   validated server-side) + company/phone/brand/quantity/state/source_url (enrichment)
   + company_website (honeypot). Responses: 200 {ok:true} · 400 {ok:false,error,fields}
   · 429 rate-limited · 500 server.

   Deliberately separate from the RFQ modal's inline handler in
   sections/category-products.liquid: that modal is the highest-value lead path on 367
   live collection pages, so this change does not refactor it. Bound by attribute
   ([data-vm-sourcing-form]) rather than id so more than one can exist on a page.

   A failed send must NEVER look like a success — an engineer who thinks their spec was
   received and hears nothing back is a lead lost twice. Errors surface inline with the
   phone fallback.

   BOT CHECK (added Aug 11 2026): the backend reads the Turnstile token as
   `turnstile_token || cf-turnstile-response`. TURNSTILE_SECRET is set in production, so a
   MISSING token scores as a FAILED bot check — the lead is still accepted (not rejected),
   but stamped "⚠️ FAILED BOT CHECK" in Pipedrive, alert-emailed, and SKIPPED by Klaviyo.
   This handler therefore renders the widget and sends the token. It must NEVER refuse to
   submit because the token is absent or expired: that would convert a backend policy of
   "accept and flag" into a client-side hard block, and lose the lead outright.

   ONE TOKEN PER SUBMIT (Oct 30 2026): a token verifies exactly once. Re-sending it (a
   resubmit after an error, from the same page load) comes back `timeout-or-duplicate`,
   which the backend files exactly like a missing token: "⚠️ FAILED BOT CHECK". Neither is
   safe; only a valid, unsent token is. So, through window.vmTurnstile
   (assets/vm-turnstile.js): before sending, wait up to TS_WAIT_MS of wall time for an
   UNSPENT token, and only after that send without one (flagged, but not lost); mark the
   token spent when it is sent; and reset THIS form's widget after EVERY response —
   success, server error, network error.
   The spent token is also stripped from the FormData copy of the hidden
   `cf-turnstile-response` input, so the backend's fallback cannot pick it up.

   Vanilla JS only. No globals, no libraries, no alert()/confirm(). */
(function () {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var PHONE_RE = /^[0-9()+\-.\s]{7,}$/;
  var PHONE = '<a href="tel:8888258800">888-825-8800</a>';
  var TURNSTILE_API = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var TS_WAIT_MS = 4000; // max wait for a fresh Turnstile token; then send (flagged) rather than lose the lead

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* "call X or email Y" tail for every failure message. The address comes from the
     form's data-fallback-email (theme setting footer_email, default info@valveman.com)
     so it stays editor-controlled rather than hardcoded here. */
  function contactTail(form) {
    var email = (form.getAttribute('data-fallback-email') || '').trim();
    var out = 'call ' + PHONE;
    if (email) out += ' or email <a href="mailto:' + esc(email) + '">' + esc(email) + '</a>';
    return out;
  }

  /* Idempotent <script> injection.
     On an empty collection page this form coexists with the RFQ modal from
     sections/category-products.liquid / brand-products.liquid — the empty-state branch
     closes BEFORE the modal markup — and that section already emits vm-turnstile.js and
     the Cloudflare API. Loading either a second time would double-initialise Turnstile,
     so each is injected only when no matching <script> is already in the document.
     Matching is a substring so the theme asset's cache-busting query string is ignored.
     This runs from a deferred script, i.e. after parsing, so the section's own tags are
     already in the DOM to be found. */
  function ensureScript(match, src, isAsync) {
    if (document.querySelector('script[src*="' + match + '"]')) return;
    var s = document.createElement('script');
    s.src = src;
    s.defer = true;
    if (isAsync) s.async = true;
    document.head.appendChild(s);
  }

  /* Returns this form's own Turnstile container, or null when the site key is blank
     (bot check off) — never throws, never blocks. */
  function setupTurnstile(form) {
    var box = form.querySelector('[data-vm-sourcing-turnstile]');
    if (!box) return null;

    var src = form.getAttribute('data-turnstile-src');
    if (src) ensureScript('vm-turnstile.js', src, false);
    ensureScript('challenges.cloudflare.com/turnstile', TURNSTILE_API, true);

    /* vm-turnstile.js polls for the API and then renders every visible .cf-turnstile, so
       it normally picks this widget up on its own boot. These nudges cover the case where
       it had already booted before this widget's scripts settled. Its renderOne() is
       guarded by data-vm-ts-rendered, so repeat calls are no-ops, and it is the attribute
       vm-turnstile.js sets — read here rather than duplicating its render logic. */
    var tries = 0;
    (function nudge() {
      if (box.getAttribute('data-vm-ts-rendered')) return;
      if (window.vmTurnstileRender) { try { window.vmTurnstileRender(); } catch (e) { /* retry */ } }
      if (tries++ < 40) setTimeout(nudge, 400); // ~16s, then give up (offline / blocked)
    })();

    return box;
  }

  /* A stable id per form so an invalid field can point at its message (aria-describedby). */
  var errSeq = 0;
  function errId(form) {
    if (!form.id) form.id = 'vm-src-form-' + (++errSeq);
    return form.id + '-error';
  }
  function fieldErrId(field) { return errId(field.form) + '-' + field.name; }

  /* Send and server errors: one message at the end of the form. */
  function inlineError(form, msg) {
    var el = form.querySelector('[data-vm-sourcing-error]');
    if (!el) {
      el = document.createElement('p');
      el.id = errId(form);
      el.className = 'vm-src__error';
      el.setAttribute('data-vm-sourcing-error', '');
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.innerHTML = msg;
    el.hidden = false;
    return el;
  }

  function clearError(form) {
    var el = form.querySelector('[data-vm-sourcing-error]');
    if (el) el.hidden = true;
  }

  /* A field's own message goes right under it (the end of the form can be off-screen on a
     phone). Every invalid field gets one at once, and each clears only when the visitor
     types in that field. */
  function fieldError(field, msg) {
    var id = fieldErrId(field);
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement('p');
      el.id = id;
      el.className = 'vm-src__error';
      el.setAttribute('data-vm-field-error', '');
      el.setAttribute('role', 'alert');
      field.insertAdjacentElement('afterend', el);
      field.addEventListener('input', function () { markField(field, false); el.hidden = true; });
    }
    el.textContent = msg;
    el.hidden = false;
    markField(field, true);
  }

  function markField(field, bad) {
    if (!field || !field.form) return;
    var id = fieldErrId(field);
    var desc = (field.getAttribute('aria-describedby') || '').split(' ').filter(function (t) { return t && t !== id; });
    if (bad) { field.setAttribute('aria-invalid', 'true'); desc.push(id); }
    else field.removeAttribute('aria-invalid');
    if (desc.length) field.setAttribute('aria-describedby', desc.join(' ')); else field.removeAttribute('aria-describedby');
  }

  /* Client-side mirror of the server's required-field rules so the visitor gets
     feedback without a round trip. The server remains the authority.
     Returns every invalid field with its message, in form order. */
  function invalidFields(form) {
    var f = form.elements;
    var name = f.namedItem('name');
    var email = f.namedItem('email');
    var msg = f.namedItem('message');
    var phone = f.namedItem('phone');
    [name, email, phone, msg].forEach(function (el) {
      if (!el) return;
      markField(el, false);
      var shown = document.getElementById(fieldErrId(el));
      if (shown) shown.hidden = true;
    });

    var bad = [];
    if (name && !String(name.value).trim()) bad.push([name, 'Please add your name so we know who to reply to.']);
    if (email && !EMAIL_RE.test(String(email.value).trim())) bad.push([email, 'Please enter a valid business email address.']);
    // Phone is required on every lead form (Gil, Oct 2026).
    var pv = phone ? String(phone.value).trim() : '';
    if (phone && (!PHONE_RE.test(pv) || pv.replace(/\D/g, '').length < 7)) bad.push([phone, pv ? 'Please enter a valid phone number.' : 'Please add a phone number so we can reach you.']);
    if (msg && !String(msg.value).trim()) bad.push([msg, 'Tell us what you need — even a rough spec is enough to start.']);
    return bad;
  }

  function bind(form) {
    if (form.getAttribute('data-vm-bound') === '1') return;
    form.setAttribute('data-vm-bound', '1');

    var endpoint = form.getAttribute('data-endpoint');
    var zone = form.closest('[data-vm-sourcing-zone]');
    var card = form.closest('.vm-src__card');
    var success = card && card.querySelector('[data-vm-sourcing-panel="success"]');
    var btn = form.querySelector('[data-vm-sourcing-submit]');
    var tsBox = setupTurnstile(form);
    var CONTACT = contactTail(form);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.getAttribute('data-vm-sending') === '1') return; // double-submit guard

      var bad = invalidFields(form);
      if (bad.length) {
        clearError(form);
        bad.forEach(function (b) { fieldError(b[0], b[1]); });
        if (bad[0][0].focus) bad[0][0].focus();
        return;
      }
      clearError(form);

      var body = {};
      new FormData(form).forEach(function (v, k) { body[k] = v; });
      body.source_url = window.location.href;

      /* Bot-check token. The widget lives inside this form, so Turnstile's hidden
         `cf-turnstile-response` input is in the FormData above. It is REPLACED here by
         the widget's current UNSPENT token (or removed when there is none), and the same
         token is sent as `turnstile_token`, which the backend prefers. A stale input
         value is exactly the spent token this guards against.
         Every Turnstile call in this file is scoped to tsBox. With the RFQ modal's widget
         on the same page, a bare getResponse()/reset() is ambiguous and could read the
         modal's token into this payload or clobber its challenge.
         A missing token does not block the submit, but it is NOT harmless: the backend
         files it as "⚠️ FAILED BOT CHECK" just like a spent one. It is the last resort
         after whenToken's wait, chosen only because refusing would lose the lead. */
      form.setAttribute('data-vm-sending', '1');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }

      var restore = function () {
        form.removeAttribute('data-vm-sending');
        if (btn) { btn.disabled = false; btn.textContent = label; }
      };
      // Reset only while the widget holds the token already sent: never discard a valid one.
      var tsReset = function () { if (tsBox && window.vmTurnstile && window.vmTurnstile.isSpent(tsBox)) window.vmTurnstile.reset(tsBox); };

      if (tsBox && window.vmTurnstile) window.vmTurnstile.whenToken(tsBox, TS_WAIT_MS, send);
      else send(null);

      function send(tsToken) {
      if (tsToken !== null) {
        delete body['cf-turnstile-response'];
        if (tsToken) {
          body.turnstile_token = tsToken;
          body['cf-turnstile-response'] = tsToken;
          window.vmTurnstile.spend(tsBox, tsToken);
        }
      }
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (j) {
            if (!r.ok || !j.ok) {
              var err = new Error('HTTP ' + r.status);
              err.status = r.status;
              err.fields = j.fields;
              throw err;
            }
            tsReset();
            if (zone) zone.hidden = true;
            if (success) { success.hidden = false; success.focus(); }
          });
        })
        .catch(function (err) {
          tsReset(); // HTTP error or network failure — the token sent is spent either way
          restore();
          var status = err && err.status;
          var fields = (err && err.fields) || [];
          if (status === 429) {
            inlineError(form, 'Too many requests from this connection — wait a moment and try again, or ' + CONTACT + '.');
          } else if (status === 400 && fields.indexOf && fields.indexOf('turnstile') !== -1) {
            inlineError(form, 'The bot check didn’t pass — please try again, or ' + CONTACT + '.');
          } else if (status === 400) {
            inlineError(form, 'Please double-check your name, email, and request details, or ' + CONTACT + '.');
          } else {
            inlineError(form, 'Something went wrong sending your request. Please try again, ' + CONTACT + ' — we don’t want to lose your spec.');
          }
        });
      }
    });
  }

  function init() {
    var forms = document.querySelectorAll('[data-vm-sourcing-form]');
    for (var i = 0; i < forms.length; i++) bind(forms[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
